<script setup lang="ts">
// components
import SvgIcon from '~/components/SvgIcon.vue';

// composables
import { useClipboard } from '@vueuse/core';

const DURATION = 750;

const props = defineProps<{ source: string }>();

const { copy, copied } = useClipboard({
  source: props.source,
  copiedDuring: DURATION,
  legacy: true,
});

const clickHandler = () => {
  if (!copied.value) {
    copy(props.source);
  }
};
</script>

<template>
  <div data-component-name="CopyToClipboard">
    <transition :duration="DURATION" name="tooltip">
      <div v-show="copied" class="tooltip">
        <SvgIcon name="check" />

        <span class="nowrap">
          {{ $t('Link_copied') }}
        </span>
      </div>
    </transition>

    <button @click="clickHandler">
      <SvgIcon name="link" />
    </button>
  </div>
</template>

<style scoped lang="scss">
@use '_/fn';
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/flex';
@import '_/transitions';

$padding: 0.5rem;

.tooltip-enter-active,
.tooltip-leave-active {
  position: absolute;
  left: 50%;
}

.tooltip-enter-active {
  transition: all 0.3s ease;
}

.tooltip-enter-from {
  opacity: 0;
  top: 50%;
  transform: translate(-50%, -50%);
}

.tooltip-enter-to {
  opacity: 1;
  top: 0;
  transform: translate(-50%, calc(-100% + $padding));
}

.tooltip-leave-active {
  transition: 0.15s opacity 0.3s ease;
}

.tooltip-leave-from {
  opacity: 1;
  top: 0;
  transform: translate(-50%, calc(-100% + $padding));
}

.tooltip-leave-to {
  opacity: 0;
  top: 0;
  transform: translate(-50%, calc(-100% + $padding));
}

[data-component-name='CopyToClipboard'] {
  position: relative;
  width: max-content;

  .tooltip {
    pointer-events: none;

    @include flex.center-center;

    gap: 0 0.25rem;

    svg {
      @include size.fixed(1.5rem);

      fill: fn.token('icon-secondary');
    }

    span {
      @include typo.subtitle-3;

      color: fn.token('text-secondary');
    }
  }

  button {
    display: flex;
    padding: $padding;

    svg {
      @include size.fixed(1.5rem);

      fill: fn.token('icon-primary');
    }
  }
}
</style>
